import { InteractionStatus } from '@azure/msal-browser';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal
} from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { toast } from 'sonner';

import Committees from './pages/Committees';
import CustomerRequestsPage from './pages/CustomerRequests';
import InvestmentsDirectIdIcVoting from './pages/investments/direct/[id]/ic-voting';
import OAuthCallback from './pages/OAuthCallback';

import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { Spinner } from '@/components/ui/spinner';
import { MainSidebarProvider } from '@/contexts/MainSidebarContext.tsx';
import { useOdooAuth } from '@/contexts/OdooAuthContext';
import { loginRequest } from '@/lib/msalConfig';
import { authenticateWithOdoo, checkOdooSession } from '@/lib/odooAuth';
import FirmChatbot from '@/pages/firm/chatbot';
import FirmCrmAccounts from '@/pages/firm/crm-accounts';
import FirmCrmAccountDetail from '@/pages/firm/crm-accounts/[id]';
import FirmCrmContacts from '@/pages/firm/crm-contacts';
import FirmCrmContactDetail from '@/pages/firm/crm-contacts/[id]';
import FirmCrmViews from '@/pages/firm/crm-views';
import FirmHome from '@/pages/firm/home';
import Investments from '@/pages/Investments';
import InvestmentsDirect from '@/pages/investments/direct';
import InvestmentsDirectId from '@/pages/investments/direct/[id]';
import InvestmentsDirectIdAllocations from '@/pages/investments/direct/[id]/allocations';
import InvestmentsDirectIdAllocationDetails from '@/pages/investments/direct/[id]/allocations/[allocationId]';
import InvestmentsDirectIdAppendices from '@/pages/investments/direct/[id]/appendices';
import EditAppendixPage from '@/pages/investments/direct/[id]/appendices/EditAppendixPage';
import InvestmentsDirectIdCompany from '@/pages/investments/direct/[id]/company';
import InvestmentsDirectIdDealCalculator from '@/pages/investments/direct/[id]/deal-calculator';
import InvestmentsDirectIdDealCalculatorDetails from '@/pages/investments/direct/[id]/deal-calculator/[dealCalculatorId]';
import InvestmentsDirectIdDealCalculatorNewCalculation from '@/pages/investments/direct/[id]/deal-calculator/new-calculation';
import InvestmentsDirectIdDealScore from '@/pages/investments/direct/[id]/deal-score';
import InvestmentsDirectIdDealScoreDetails from '@/pages/investments/direct/[id]/deal-score/[dealScoreId]';
import InvestmentsDirectIdDealScoreAddScore from '@/pages/investments/direct/[id]/deal-score/add-score';
import InvestmentsDirectIdDueDiligence from '@/pages/investments/direct/[id]/due-diligence';
import InvestmentsDirectIdFeedback from '@/pages/investments/direct/[id]/feedback';
import InvestmentsDirectIdFinancials from '@/pages/investments/direct/[id]/financials';
import InvestmentsDirectIdHighlightsAndRisks from '@/pages/investments/direct/[id]/highlights-and-risks';
import InvestmentsDirectIdIndustry from '@/pages/investments/direct/[id]/industry';
import InvestmentsDirectIdMarketPosition from '@/pages/investments/direct/[id]/market-position';
import InvestmentsDirectIdOverview from '@/pages/investments/direct/[id]/overview';
import InvestmentsDirectIdReports from '@/pages/investments/direct/[id]/reports';
import InvestmentsDirectIdTalent from '@/pages/investments/direct/[id]/talent';
import InvestmentsDirectIdTransactionStructure from '@/pages/investments/direct/[id]/transaction-structure';
import PipelineAnalytics from '@/pages/investments/PipelineAnalytics';
import Portfolio from '@/pages/Portfolio';
import PortfolioReporting from '@/pages/portfolio/reporting';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SoonPage } from '@/pages/SoonPage';
import Workflows from '@/pages/Workflows';

import 'yet-another-react-lightbox/styles.css';

function DepartmentGuard({
  children,
  department: requiredDepartment
}: {
  children: React.ReactNode;
  department: string;
}) {
  const { department } = useOdooAuth();

  if (department !== requiredDepartment) {
    return <Navigate replace to="/investments/direct" />;
  }

  return <>{children}</>;
}

// Star Mountain Capital logo
const SMC_LOGO_URL = '/smc-logo.svg';

const LoginPage = () => {
  const { inProgress, instance } = useMsal();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async () => {
    if (inProgress === InteractionStatus.None && !isLoggingIn) {
      setIsLoggingIn(true);
      try {
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        console.error('Login error:', error);
        setIsLoggingIn(false);
      }
    }
  };

  const isLoading = inProgress !== InteractionStatus.None || isLoggingIn;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-xl space-y-8 rounded-2xl bg-white p-12 shadow-2xl">
        <div className="flex flex-col items-center space-y-6 text-center">
          <img
            alt="Star Mountain Capital"
            className="h-[80px] w-auto"
            src={SMC_LOGO_URL}
          />
          <div className="space-y-3">
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome to Opstream
            </h1>
            <p className="text-sm leading-relaxed text-gray-600">
              Your unified operations platform for investment management,
              portfolio monitoring, and workflow automation
            </p>
          </div>
        </div>

        <div className="space-y-4 pt-4">
          <Button
            className="relative w-full"
            disabled={isLoading}
            onClick={() => {
              void handleLogin();
            }}
            size="lg"
            type="button"
          >
            {isLoading && (
              <Spinner className="absolute inset-0 m-auto size-5" />
            )}
            <span className={isLoading ? 'opacity-0' : undefined}>
              Sign in with Microsoft
            </span>
          </Button>
          {isLoading && (
            <p className="text-center text-sm text-gray-500">
              Redirecting to Microsoft login...
            </p>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} Star Mountain Capital
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Component that handles Odoo authentication after MSAL login
 */
const OdooAuthHandler = ({ children }: { children: React.ReactNode }) => {
  const { accounts, instance } = useMsal();
  const [isAuthenticatingWithOdoo, setIsAuthenticatingWithOdoo] =
    useState(true);
  const [odooAuthError, setOdooAuthError] = useState<null | string>(null);
  const { setOdooAuth } = useOdooAuth();

  useEffect(() => {
    const authenticateOdoo = async () => {
      if (accounts.length === 0) {
        setIsAuthenticatingWithOdoo(false);
        setOdooAuth(null);
        const preloader = document.getElementById('preloader');
        if (preloader) {
          preloader.remove();
        }
        return;
      }

      try {
        // Check if there's already a valid session before exchanging tokens
        const sessionResponse = await checkOdooSession();

        if (sessionResponse.success && sessionResponse.is_authenticated) {
          setOdooAuth(sessionResponse);
          setIsAuthenticatingWithOdoo(false);
          const preloader = document.getElementById('preloader');
          if (preloader) {
            preloader.remove();
          }
          return;
        }

        // No valid session - authenticate with Azure tokens
        if (sessionResponse.error) {
          console.log('Session check failed:', sessionResponse.error);
        } else if (!sessionResponse.is_authenticated) {
          console.log('Session expired or user not authenticated');
        }

        console.log('Authenticating with Azure tokens...');
        const account = accounts[0];
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account
        });

        const odooResponse = await authenticateWithOdoo(
          response.idToken,
          response.accessToken
        );

        if (odooResponse.error) {
          console.error('Platform authentication failed:', odooResponse.error);

          if (odooResponse.error === 'token_expired') {
            setOdooAuthError(
              'Your authentication has expired. Please refresh the page to sign in again.'
            );
          } else if (
            odooResponse.error === 'user_not_found' ||
            odooResponse.error === 'signin_failed'
          ) {
            setOdooAuthError(
              'Your account is not yet set up in Opstream. Please contact an administrator to request access.'
            );
          } else if (odooResponse.error === 'authentication_failed') {
            const errorDesc = odooResponse.error_description ?? '';
            if (errorDesc.includes('Invalid audience')) {
              setOdooAuthError(
                'Authentication configuration error. Please contact support or try signing out and signing in again.'
              );
            } else if (
              errorDesc.includes('expired') ||
              errorDesc.includes('Expired') ||
              errorDesc.includes('Signature has expired')
            ) {
              setOdooAuthError(
                'Your authentication has expired. Please refresh the page to sign in again.'
              );
            } else {
              setOdooAuthError(
                'Authentication failed. Please try signing out and signing in again, or contact support if the issue persists.'
              );
            }
          } else {
            setOdooAuthError(
              'Unable to complete authentication. Please try again or contact support if the issue persists.'
            );
          }
          toast.error('Unable to access your workspace. Please try again.');
        } else {
          console.log('Successfully authenticated');
          setOdooAuth(odooResponse);
          toast.success(
            `Welcome back, ${odooResponse.name ?? odooResponse.login ?? 'User'}!`
          );
        }
      } catch (error) {
        console.error('Error during platform authentication:', error);
        setOdooAuthError(
          'We encountered an issue setting up your workspace. Please try again.'
        );
        toast.error('Unable to access your workspace');
      } finally {
        setIsAuthenticatingWithOdoo(false);
        const preloader = document.getElementById('preloader');
        if (preloader) {
          preloader.remove();
        }
      }
    };

    void authenticateOdoo();
  }, [accounts, instance, setOdooAuth]);

  if (isAuthenticatingWithOdoo) {
    return null;
  }

  const handleLogout = () => {
    void instance.logoutRedirect({
      postLogoutRedirectUri:
        import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin
    });
  };

  if (odooAuthError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="w-full max-w-md space-y-8 rounded-2xl bg-white p-10 shadow-2xl">
          <div className="flex flex-col items-center space-y-6 text-center">
            <img
              alt="Star Mountain Capital"
              className="h-[60px] w-auto"
              src={SMC_LOGO_URL}
            />
            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-gray-900">
                Unable to Access Opstream
              </h2>
              <p className="text-sm text-gray-600">{odooAuthError}</p>
            </div>
            <div className="flex w-full flex-col gap-3">
              <Button
                className="w-full"
                onClick={() => {
                  window.location.reload();
                }}
                size="lg"
              >
                Try Again
              </Button>
              <Button
                className="w-full"
                onClick={handleLogout}
                size="lg"
                variant="outline"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const App = () => {
  useEffect(() => {
    const inlineCss = document.getElementById('inline-css');
    if (inlineCss) {
      inlineCss.remove();
    }

    const preloader = document.getElementById('preloader');
    if (preloader) {
      preloader.remove();
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthenticatedTemplate>
        <OdooAuthHandler>
          <MainSidebarProvider>
            <Routes>
              <Route
                element={
                  <Navigate
                    replace
                    to="/investments/direct"
                  />
                }
                path="/"
              />
              <Route element={<Layout />}>
                <Route
                  element={<FirmHome />}
                  path="/firm/home"
                />
                <Route
                  element={<FirmChatbot />}
                  path="/firm/chatbot"
                />
                {/* CRM routes — disabled until feature is re-enabled
                <Route
                  element={<FirmCrmAccounts />}
                  path="/firm/crm-accounts"
                />
                <Route
                  element={<FirmCrmAccountDetail />}
                  path="/firm/crm-accounts/:id/*"
                />
                <Route
                  element={<FirmCrmContacts />}
                  path="/firm/crm-contacts"
                />
                <Route
                  element={<FirmCrmContactDetail />}
                  path="/firm/crm-contacts/:id/*"
                />
                <Route
                  element={<SoonPage featureName="Investor Operations" />}
                  path="/investors/operations"
                />
                <Route
                  element={<SoonPage featureName="Investor Operations" />}
                  path="/investors/operations/:id"
                />
                <Route
                  element={<FirmCrmViews />}
                  path="/firm/crm-views"
                />
                */}
                <Route
                  element={<Workflows />}
                  path="/firm/workflows/todo"
                />
                <Route
                  element={<Workflows />}
                  path="/firm/workflows/timeline"
                />
                <Route
                  element={<Workflows />}
                  path="/firm/workflows/processes"
                />
                <Route
                  element={<InvestmentsDirect />}
                  path="/investments/direct"
                />
                <Route
                  element={<InvestmentsDirectId />}
                  path="/investments/direct/:id"
                >
                  <Route
                    element={<InvestmentsDirectIdOverview />}
                    path="overview"
                  />
                  <Route
                    element={<InvestmentsDirectIdHighlightsAndRisks />}
                    path="highlights-and-risks"
                  />
                  <Route
                    element={<InvestmentsDirectIdTransactionStructure />}
                    path="transaction-structure"
                  />
                  <Route
                    element={<InvestmentsDirectIdCompany />}
                    path="company"
                  />
                  <Route
                    element={<InvestmentsDirectIdFinancials />}
                    path="financials"
                  />
                  <Route
                    element={<InvestmentsDirectIdTalent />}
                    path="talent"
                  />
                  <Route
                    element={<InvestmentsDirectIdIndustry />}
                    path="industry"
                  />
                  <Route
                    element={<InvestmentsDirectIdMarketPosition />}
                    path="market-position"
                  />
                  <Route
                    element={<InvestmentsDirectIdAppendices />}
                    path="appendices"
                  />
                  <Route
                    element={<EditAppendixPage />}
                    path="appendices/:appendixId"
                  />
                  <Route
                    element={<InvestmentsDirectIdDealScore />}
                    path="deal-score"
                  />
                  <Route
                    element={<InvestmentsDirectIdDealScoreAddScore />}
                    path="deal-score/add-score"
                  />
                  <Route
                    element={<InvestmentsDirectIdDealScoreDetails />}
                    path="deal-score/:dealScoreId"
                  />
                  <Route
                    element={<InvestmentsDirectIdAllocations />}
                    path="allocations"
                  />
                  <Route
                    element={<InvestmentsDirectIdAllocationDetails />}
                    path="allocations/:allocationId"
                  />
                  <Route
                    element={<InvestmentsDirectIdDueDiligence />}
                    path="due-diligence"
                  />
                  <Route
                    element={<InvestmentsDirectIdFeedback />}
                    path="feedback"
                  />
                  <Route
                    element={<InvestmentsDirectIdReports />}
                    path="reports"
                  />
                  <Route
                    element={<InvestmentsDirectIdDealCalculator />}
                    path="deal-calculator"
                  />
                  <Route
                    element={<InvestmentsDirectIdDealCalculatorDetails />}
                    path="deal-calculator/:dealCalculatorId"
                  />
                  <Route
                    element={
                      <InvestmentsDirectIdDealCalculatorNewCalculation />
                    }
                    path="deal-calculator/new-calculation"
                  />
                  <Route
                    element={<InvestmentsDirectIdIcVoting />}
                    path="ic-voting"
                  />
                </Route>
                <Route
                  element={<Investments />}
                  path="/investments/secondary"
                />
                <Route
                  element={<Investments />}
                  path="/investments/pipelines"
                />
                <Route
                  element={<PipelineAnalytics />}
                  path="/investments/pipeline-analytics"
                />
                <Route
                  element={<Portfolio />}
                  path="/portfolio/monitoring"
                />
                <Route
                  element={<PortfolioReporting />}
                  path="/portfolio/reporting"
                />
                <Route
                  element={<Portfolio />}
                  path="/portfolio/rocky"
                />
                <Route
                  element={<Investments />}
                  path="/fund-management/funds"
                />
                <Route
                  element={
                    <DepartmentGuard department="Financial Operations & Treasury Management">
                      <Investments />
                    </DepartmentGuard>
                  }
                  path="/fund-management/allocations"
                />
                <Route
                  element={<Investments />}
                  path="/fund-management/relative-value"
                />
                <Route
                  element={
                    <DepartmentGuard department="Administration">
                      <Committees />
                    </DepartmentGuard>
                  }
                  path="/committees"
                />
                <Route
                  element={<CustomerRequestsPage />}
                  path="/customer-requests"
                />
                <Route
                  element={<NotFoundPage />}
                  path="*"
                />
              </Route>
              <Route
                element={<OAuthCallback />}
                path="/oauth/omni/callback"
              />
            </Routes>
          </MainSidebarProvider>
        </OdooAuthHandler>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>

      <Toaster />
    </BrowserRouter>
  );
};

export default App;
